import { toCanvas } from 'html-to-image';
import { PDFDocument } from 'pdf-lib';

interface ExportPdfOptions {
    fileName: string;
    // Predicate evaluated for each node during DOM capture: return `true` to
    // exclude the node from the rendered output.
    ignoreElements?: (el: HTMLElement) => boolean;
    scale?: number;
    orientation?: 'portrait' | 'landscape';
    marginPt?: number;
}

const LETTER_PORTRAIT = { w: 612, h: 792 };
const LETTER_LANDSCAPE = { w: 792, h: 612 };

// CSS px → PDF pt at standard 96/72 DPI. One CSS pixel renders as 0.75 pt,
// which means a canvas captured at pixelRatio N has a "natural" PDF scale
// of (PT_PER_CSS_PX / N) — i.e. the size that matches what the user saw on
// screen.
const PT_PER_CSS_PX = 72 / 96;

export async function exportElementToPdf(
    element: HTMLElement,
    options: ExportPdfOptions
): Promise<void> {
    const {
        fileName,
        ignoreElements,
        scale = 2,
        orientation = 'landscape',
        marginPt = 24,
    } = options;

    // Reset scroll so sticky-positioned columns capture at their natural position.
    const prevScrollLeft = element.scrollLeft;
    const prevScrollTop = element.scrollTop;
    element.scrollLeft = 0;
    element.scrollTop = 0;

    let canvas: HTMLCanvasElement;
    try {
        canvas = await toCanvas(element, {
            pixelRatio: scale,
            backgroundColor: '#ffffff',
            // html-to-image's filter returns `true` to include a node; invert
            // the ignoreElements predicate so callers keep the same semantics.
            filter: ignoreElements ? (node) => !ignoreElements(node) : undefined,
            width: element.scrollWidth,
            height: element.scrollHeight,
            // The export host lives off-screen via position:fixed + a negative
            // transform. html-to-image clones the element into an SVG
            // foreignObject and preserves those styles, which shifts the
            // rendered content off the canvas (resulting in a blank PDF).
            // Reset position/transform on the clone so it renders at (0,0).
            style: {
                position: 'static',
                left: 'auto',
                top: 'auto',
                transform: 'none',
            },
        });
    } finally {
        element.scrollLeft = prevScrollLeft;
        element.scrollTop = prevScrollTop;
    }

    const page = orientation === 'landscape' ? LETTER_LANDSCAPE : LETTER_PORTRAIT;
    const usableW = page.w - 2 * marginPt;
    const usableH = page.h - 2 * marginPt;

    // Cap the scale at "natural" (1:1 with what the user saw on screen) so a
    // capture narrower than the page doesn't get blown up to fill the width.
    // Captures wider than the page still scale down to fit.
    const naturalScale = PT_PER_CSS_PX / scale;
    const fitToWidthScale = usableW / canvas.width;
    const fitScale = Math.min(fitToWidthScale, naturalScale);
    const drawW = canvas.width * fitScale;
    const fullDrawH = canvas.height * fitScale;

    const pdfDoc = await PDFDocument.create();

    if (fullDrawH <= usableH) {
        const pngBytes = await canvasToPngBytes(canvas);
        const image = await pdfDoc.embedPng(pngBytes);
        const pdfPage = pdfDoc.addPage([page.w, page.h]);
        pdfPage.drawImage(image, {
            x: marginPt,
            y: page.h - marginPt - fullDrawH,
            width: drawW,
            height: fullDrawH,
        });
    } else {
        // Table is taller than one page once scaled to fit width — slice the
        // captured canvas vertically into page-height chunks.
        const sliceCanvasHeight = Math.floor(usableH / fitScale);
        let offsetY = 0;
        while (offsetY < canvas.height) {
            const sliceH = Math.min(sliceCanvasHeight, canvas.height - offsetY);
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceH;
            const ctx = sliceCanvas.getContext('2d');
            if (!ctx) throw new Error('Failed to get 2d context for PDF slice');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

            const pngBytes = await canvasToPngBytes(sliceCanvas);
            const image = await pdfDoc.embedPng(pngBytes);
            const pdfPage = pdfDoc.addPage([page.w, page.h]);
            const drawH = sliceH * fitScale;
            pdfPage.drawImage(image, {
                x: marginPt,
                y: page.h - marginPt - drawH,
                width: drawW,
                height: drawH,
            });

            offsetY += sliceH;
        }
    }

    const pdfBytes = await pdfDoc.save();
    triggerDownload(pdfBytes, fileName);
}

function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
            if (!blob) {
                reject(new Error('Canvas toBlob returned null'));
                return;
            }
            const buf = await blob.arrayBuffer();
            resolve(new Uint8Array(buf));
        }, 'image/png');
    });
}

function triggerDownload(pdfBytes: Uint8Array, fileName: string): void {
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
